import { MdAccessTime, MdCalendarToday, MdOutlineLanguage, MdInfo } from 'react-icons/md';
import TextWithIcon from '../../../../components/Common/TextWithIcon';
import OdsChips from '../../../../components/Mappers/OdsChips';
import PriorityGroupChips from '../../../../components/Mappers/PriorityGroupsChips';
import { OpenDataIndicator } from '../../../../interfaces/OpenData';
import { FUENTE_OPEN_DATA, INDICATOR_TYPE_STRUCTURAL } from '../../../../utils/const';
import { getPeriodoEvaluado } from '../../../../utils/getEvaluatedPeriod';
import ChipBreadcrumb from '../../../../components/Chips/ChipBreadcrumb';
import CustomCheckbox from '../../../../components/Inputs/Checkbox/CustomCheckbox';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import { toggleIndicatorSelection } from '../../../../redux/slices/openData/openDataFiltersSlice';
import clsx from 'clsx';
import DownloadPDFWithFetch from '../../../../components/DownloadPDF/DownloadPDFWithFetch';

interface IndicatorCardProps {
    indicator: OpenDataIndicator;
}

export default function IndicatorCard({ indicator }: IndicatorCardProps) {
    const dispatch = useAppDispatch();
    const selectedIndicators = useAppSelector((state) => state.open_data_filters.selectedIndicators);
    const isSelected = selectedIndicators.some(i => i.id === indicator.id);

    const { titulo, ods, grupos_prioritarios, descripcion, actualizacion, tipo_indicador, evaluaciones, sancion, id, nota_metodologica } = indicator;

    const handleToggle = () => {
        dispatch(toggleIndicatorSelection(indicator));
    };

    return (
        <div
            className={clsx(
                'w-full bg-primary-100 border rounded-md transition-all duration-300 ease-out',
                {
                    'border-system-light-20': !isSelected,
                    'border-system-light-50 shadow-custom-dual': isSelected,
                }
            )}
        >
            <div className="my-6 mx-4 flex flex-col gap-3">
                <div className="flex justify-between items-start">
                    <h2 className="text-brand-green-30 text-tlarge">{titulo}</h2>
                    <div onClick={(e) => e.stopPropagation()}>
                        <CustomCheckbox
                            label=""
                            id={id.toString()}
                            checked={isSelected}
                            onChange={handleToggle}
                            name={titulo.toLocaleLowerCase()}
                        />
                    </div>
                </div>
                <div className="flex flex-wrap gap-2">
                    {indicator.breadcrumbs.split('/').map((breadcrumb, index) => (
                        <ChipBreadcrumb text={breadcrumb.trim()} key={`${breadcrumb.trim()}-${index}`} />
                    ))}
                </div>
                <PriorityGroupChips groups={grupos_prioritarios ?? []} />
                <OdsChips ods={ods ?? []} />
                <p className="text-system-light-40 text-bmedium">{descripcion}</p>
                <TextWithIcon
                    text={`Última actualización: ${actualizacion}`}
                    icon={MdAccessTime}
                    iconColor="#264654"
                />
                {
                    tipo_indicador !== INDICATOR_TYPE_STRUCTURAL &&
                    <TextWithIcon
                        text={`Periodo: ${tipo_indicador !== INDICATOR_TYPE_STRUCTURAL ? getPeriodoEvaluado(evaluaciones || {}) : 'sin datos'}`}
                        icon={MdCalendarToday}
                        iconColor="#264654"
                    />
                }
                {tipo_indicador === INDICATOR_TYPE_STRUCTURAL && sancion && (
                    <TextWithIcon
                        text={`Año de sanción: ${new Date(sancion).getFullYear()}`}
                        icon={MdCalendarToday}
                        iconColor="#264654"
                    />
                )}
                {nota_metodologica && (
                    <DownloadPDFWithFetch
                        fileName={nota_metodologica ?? ''}
                        label="Nota metodológica"
                        className='text-primary-40 !underline !p-0 !h-max'
                        icon={MdInfo}
                        iconSize={16}
                        module="Evaluador"
                        source={titulo}
                        variant='none'
                    />
                )}
                <TextWithIcon
                    text={`${FUENTE_OPEN_DATA}`}
                    icon={MdOutlineLanguage}
                    iconColor="#264654"
                />
            </div>
        </div>
    );
}